import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

import {
  AppWindow,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Gamepad2,
  GitFork,
  LayoutDashboard,
  Loader2,
  type LucideIcon,
  Maximize2,
  Pencil,
  Play,
  RotateCcw,
  Wand2,
  X,
} from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { ThinkingIndicator } from "@/components/agent/thinking-indicator";
import { usePageBar } from "@/components/layout/page-bar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuthToken } from "@/hooks/useAuthToken";
import { useConnectors } from "@/hooks/useConnectors";
import { usePermissions } from "@/hooks/usePermissions";
import { useResponsive } from "@/hooks/useResponsive";
import { builderApi } from "@/lib/api/builder";
import { getTemplateIdForTags } from "@/lib/templates";
import { cn } from "@/lib/utils";
import {
  CATEGORIES,
  extractFinalPrompt,
  getAvailableConnectorIds,
  getPromptById,
  isPromptAvailable,
} from "@/lib/prompt-lib-utils";

/**
 * Markdown renderers for the read view of a prompt. Keeps the prompt scannable
 * (real bullets, bold labels, inline code) instead of a raw wall of text, using
 * theme tokens so both light and dark read cleanly.
 */
/** Icon shown on the placeholder panel for prompts that ship without a preview image. */
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "website-apps": AppWindow,
  "dashboard-feeds": LayoutDashboard,
  games: Gamepad2,
};

const promptMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-foreground/90 mb-3 last:mb-0">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 space-y-1.5 mb-3 last:mb-0 marker:text-violet-500/70">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 space-y-1.5 mb-3 last:mb-0 marker:text-violet-500/70">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-sm leading-relaxed text-muted-foreground pl-1">
      {children}
    </li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">
      {children}
    </code>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-violet-600 underline decoration-violet-400/50 underline-offset-2 break-words hover:text-violet-500 dark:text-violet-400 dark:hover:text-violet-300"
    >
      {children}
    </a>
  ),
  h1: ({ children }) => (
    <h3 className="text-sm font-semibold text-foreground mb-2 mt-4 first:mt-0">
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h3 className="text-sm font-semibold text-foreground mb-2 mt-4 first:mt-0">
      {children}
    </h3>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-foreground mb-2 mt-4 first:mt-0">
      {children}
    </h3>
  ),
};

export default function PromptDetailPage() {
  const { promptId } = useParams<{ promptId: string }>();
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { canCreateProject } = usePermissions();
  const { isMobile } = useResponsive();
  const { connectors, loading: connectorsLoading } = useConnectors();

  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  // The prompt reads clean by default; Edit swaps it to the editable textarea
  const [isEditing, setIsEditing] = useState(false);
  // Falls back to the placeholder panel if the preview asset fails to load
  const [imgError, setImgError] = useState(false);
  // Holds the pre-enhance text so the user can revert an unwanted expansion
  const [prevPrompt, setPrevPrompt] = useState<string | null>(null);
  // Lightbox for a larger, clearer look at the preview image(s)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const prompt = useMemo(() => {
    if (!promptId) return null;
    return getPromptById(promptId);
  }, [promptId]);

  // Gallery images — `previews` when present, else the single cover, else none
  const previewImages = useMemo(
    () =>
      prompt?.previews?.length
        ? prompt.previews
        : prompt?.preview
          ? [prompt.preview]
          : [],
    [prompt],
  );
  const hasGallery = previewImages.length > 1;

  const categoryLabel = useMemo(
    () => CATEGORIES.find((c) => c.id === prompt?.category)?.label ?? null,
    [prompt],
  );

  const showPrev = () =>
    setActiveIndex(
      (i) => (i - 1 + previewImages.length) % previewImages.length,
    );
  const showNext = () => setActiveIndex((i) => (i + 1) % previewImages.length);

  const [finalPrompt, setFinalPrompt] = useState(() => {
    if (!prompt) return "";
    return extractFinalPrompt(prompt.content);
  });

  // Reset the editable prompt when navigating between prompts
  useEffect(() => {
    if (prompt) {
      setFinalPrompt(extractFinalPrompt(prompt.content));
      setPrevPrompt(null);
      setIsEditing(false);
      setImgError(false);
    }
  }, [prompt]);

  // Scroll to top when page mounts
  useEffect(() => {
    requestAnimationFrame(() => {
      const mainContainer = document.querySelector("main");
      if (mainContainer) {
        mainContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    });
  }, [promptId]);

  // Guard direct URLs: if this prompt needs a connector that isn't available, send the user
  // back to the library (wait until connectors load so a valid prompt isn't bounced).
  useEffect(() => {
    if (!prompt?.connector || connectorsLoading) return;
    if (!isPromptAvailable(prompt, getAvailableConnectorIds(connectors))) {
      navigate("/dashboard/prompt-library", { replace: true });
    }
  }, [prompt, connectors, connectorsLoading, navigate]);

  // Arrow-key navigation while the lightbox gallery is open
  useEffect(() => {
    if (!isPreviewOpen || previewImages.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft")
        setActiveIndex(
          (i) => (i - 1 + previewImages.length) % previewImages.length,
        );
      else if (e.key === "ArrowRight")
        setActiveIndex((i) => (i + 1) % previewImages.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPreviewOpen, previewImages.length]);

  const handleCopyPrompt = async () => {
    if (!finalPrompt.trim()) return;

    try {
      await navigator.clipboard.writeText(finalPrompt);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleEnhance = async () => {
    if (!token || !finalPrompt.trim() || isEnhancing) return;

    setIsEnhancing(true);
    setError(null);

    try {
      const { enhancedPrompt } = await builderApi.enhancePrompt(
        token,
        finalPrompt,
      );
      if (enhancedPrompt && enhancedPrompt.trim()) {
        setPrevPrompt(finalPrompt);
        setFinalPrompt(enhancedPrompt.trim());
      }
    } catch (err) {
      console.error("Failed to enhance prompt:", err);
      setError(err instanceof Error ? err.message : "Failed to enhance prompt");
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleRevert = () => {
    if (prevPrompt === null) return;
    setFinalPrompt(prevPrompt);
    setPrevPrompt(null);
  };

  // Remixable entries fork the real App Library app via its existing modal flow
  const handleRemix = () => {
    if (!prompt?.remixAppId) return;
    navigate(`/dashboard/apps?remix=${encodeURIComponent(prompt.remixAppId)}`);
  };

  const handleRunPrompt = async () => {
    if (!token || !finalPrompt.trim()) return;

    setIsRunning(true);
    setError(null);

    try {
      // 1. Pre-flight classification
      const preFlight = await builderApi.preFlight(token, {
        prompt: finalPrompt,
        uploadedFiles: [],
      });

      // 2. Create project + dev-container
      const result = await builderApi.complete(token, {
        slug: preFlight.slug,
        templateId: getTemplateIdForTags(preFlight.tags),
        builderData: {
          fromBuilder: true,
          initialPrompt: finalPrompt,
          appType: preFlight.app_type,
          tags: preFlight.tags,
          mode: "execute",
          referenceUrls: [],
          uploadedFiles: [],
        },
      });

      // 3. Redirect to Power Editor
      navigate(result.redirect);
    } catch (err) {
      console.error("Failed to run prompt:", err);
      setError(err instanceof Error ? err.message : "Failed to create project");
      setIsRunning(false);
    }
  };

  // Stable page title; the current prompt is shown as an in-content breadcrumb
  // below (same pattern as the Projects folder view).
  usePageBar({
    title: "Prompt Library",
  });

  if (!prompt) {
    return (
      <div className="min-h-full bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Prompt not found</p>
          <Link
            to="/dashboard/prompt-library"
            className="text-primary hover:text-primary/80"
          >
            Back to Prompt Library
          </Link>
        </div>
      </div>
    );
  }

  const showImage = Boolean(prompt.preview) && !imgError;
  const PlaceholderIcon = CATEGORY_ICONS[prompt.category] ?? AppWindow;

  return (
    <div className="min-h-full bg-background text-foreground">
      <div
        className={cn(
          "w-full",
          isMobile ? "px-4 py-6" : "px-6 sm:px-8 lg:px-10 pt-6 pb-10",
        )}
      >
        {/* Breadcrumb — inline back link + current prompt (matches the Projects
            folder breadcrumb style). */}
        <div className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Link
            to="/dashboard/prompt-library"
            className="inline-flex items-center gap-2 transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            All prompts
          </Link>
          <span className="text-muted-foreground/40">/</span>
          <span className="font-medium text-foreground">{prompt.title}</span>
        </div>

        {/* HERO — preview (or branded placeholder) on the left, name + chips + CTA + prompt on the right */}
        <div className="grid grid-cols-1 gap-6 lg:gap-8 lg:grid-cols-2">
          {/* Preview (left) — the image, or a branded placeholder when a prompt has none.
              Fills the full column height (lg) so it matches the right side exactly. */}
          {/* The figure is the sized box; its contents are absolutely positioned so the image
              can NEVER drive the height. On lg the grid stretches it to match the right column
              exactly; on mobile it falls back to a 4/3 box. */}
          <figure className="relative m-0 aspect-[4/3] lg:aspect-auto overflow-hidden rounded-2xl border border-border bg-muted/60 dark:bg-muted/25">
            {showImage ? (
              <button
                type="button"
                onClick={() => {
                  setActiveIndex(0);
                  setIsPreviewOpen(true);
                }}
                className="group absolute inset-0 h-full w-full cursor-zoom-in"
                aria-label={
                  hasGallery
                    ? `Open preview gallery (${previewImages.length} images)`
                    : "Open larger preview"
                }
              >
                {/* Blurred same-image fill — no dead letterbox bars for portrait/landscape */}
                <img
                  aria-hidden
                  src={prompt.preview}
                  alt=""
                  className="pointer-events-none absolute inset-0 h-full w-full scale-110 select-none object-cover opacity-0 blur-xl dark:opacity-40"
                />
                <img
                  src={prompt.preview}
                  alt={
                    prompt.remixAppId
                      ? `${prompt.title} app`
                      : `Design reference for ${prompt.title}`
                  }
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-contain p-2"
                  onError={() => setImgError(true)}
                />
                <span className="pointer-events-none absolute right-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-1 text-[11px] font-medium text-foreground/80 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                  <Maximize2 className="h-3 w-3" />
                  Expand
                </span>
              </button>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-500/15 via-fuchsia-500/10 to-indigo-500/15 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-indigo-500/15">
                {/* soft radial glow for depth */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-1/3 left-1/2 h-2/3 w-2/3 -translate-x-1/2 rounded-full bg-violet-500/20 blur-3xl"
                />
                <PlaceholderIcon
                  className="relative h-14 w-14 text-violet-500/80 dark:text-violet-300/80"
                  strokeWidth={1.5}
                />
                {categoryLabel && (
                  <span className="relative text-sm font-medium text-muted-foreground">
                    {categoryLabel}
                  </span>
                )}
              </div>
            )}
          </figure>

          {/* Info + CTA + prompt (right) */}
          <div className="min-w-0">
            {/* Prompt title lives in the shell PageBar */}
            <p className="text-[15px] leading-relaxed text-muted-foreground">
              {prompt.description}
            </p>

            {/* Metadata chips */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs font-medium text-violet-600 dark:text-violet-400">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {prompt.stackType === "fullstack" ? "Fullstack" : "Frontend"}
              </span>
              {categoryLabel && (
                <span className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  {categoryLabel}
                </span>
              )}
            </div>

            {/* Primary action(s) */}
            <div className="mt-6 flex flex-col gap-2.5">
              {prompt.remixAppId ? (
                <>
                  <Button
                    variant="contrast"
                    size="lg"
                    className="w-full"
                    onClick={handleRemix}
                    disabled={!canCreateProject}
                    title={
                      !canCreateProject
                        ? "You don't have permission to create projects"
                        : undefined
                    }
                  >
                    <GitFork className="h-4 w-4 mr-2" />
                    Remix this app
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full"
                    onClick={handleRunPrompt}
                    disabled={
                      isRunning || !finalPrompt.trim() || !canCreateProject
                    }
                    title={
                      !canCreateProject
                        ? "You don't have permission to create projects"
                        : undefined
                    }
                  >
                    {isRunning ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating…
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Start from prompt
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contrast"
                  size="lg"
                  className="w-full"
                  onClick={handleRunPrompt}
                  disabled={
                    isRunning || !finalPrompt.trim() || !canCreateProject
                  }
                  title={
                    !canCreateProject
                      ? "You don't have permission to create projects"
                      : undefined
                  }
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Start building
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="mt-4 bg-destructive/10 border border-destructive/50 rounded-lg text-destructive p-3.5 text-sm">
                {error}
              </div>
            )}
            {/* PROMPT — lives in the right column beside the preview; reads clean by default, edit / copy / enhance in place */}
            <section className="mt-6 rounded-2xl border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-border/60 bg-muted/20">
                <span className="flex items-center gap-2.5 text-sm font-semibold">
                  <span className="h-2 w-2 rounded-[2px] bg-violet-500" />
                  Your prompt
                </span>
                <div className="flex items-center gap-2">
                  {prevPrompt !== null && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRevert}
                      title="Undo the enhancement"
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Revert
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing((v) => !v)}
                    disabled={!finalPrompt.trim()}
                    title={
                      isEditing
                        ? "Finish editing"
                        : "Edit the prompt before you build"
                    }
                  >
                    {isEditing ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        Done
                      </>
                    ) : (
                      <>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyPrompt}
                    disabled={!finalPrompt.trim()}
                  >
                    {isCopied ? (
                      <>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Copy
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEnhance}
                    disabled={isEnhancing || !finalPrompt.trim() || !token}
                    title="Let AI expand this into a clearer prompt"
                  >
                    {isEnhancing ? (
                      <ThinkingIndicator
                        label="Enhancing"
                        className="-my-0.5"
                      />
                    ) : (
                      <>
                        <Wand2 className="h-3.5 w-3.5 mr-1.5 text-violet-500 dark:text-violet-400" />
                        Enhance
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Fixed-height, scrolls internally — the panel never grows with the prompt,
                  so the right column (and the tied image) stay a consistent height. */}
              {isEditing ? (
                <Textarea
                  id="prompt-text"
                  autoFocus
                  value={finalPrompt}
                  onChange={(e) => setFinalPrompt(e.target.value)}
                  spellCheck={false}
                  className="h-[440px] resize-none rounded-none border-0 bg-transparent px-4 py-4 sm:px-5 text-sm leading-relaxed shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                <div className="h-[440px] overflow-y-auto px-4 py-4 sm:px-5 [scrollbar-width:thin]">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={promptMarkdownComponents}
                  >
                    {finalPrompt}
                  </ReactMarkdown>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Lightbox — centered viewer panel (theme-aware; page dims behind it). Fixed-size
          panel so the frame never changes per prompt; the image floats centered at natural
          size on the panel, so portrait and landscape both look right. */}
      {previewImages.length > 0 && (
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent
            hideCloseButton
            className="h-[88vh] w-[min(92vw,1100px)] max-w-none gap-0 overflow-hidden rounded-xl border border-border bg-background p-0 shadow-2xl"
          >
            <DialogTitle className="sr-only">
              {prompt.title} preview
              {hasGallery
                ? ` (${activeIndex + 1} of ${previewImages.length})`
                : ""}
            </DialogTitle>
            {/* Subtle same-image glow behind the photo for a bit of depth on the panel */}
            <img
              aria-hidden
              src={previewImages[activeIndex]}
              alt=""
              className="pointer-events-none absolute inset-0 h-full w-full scale-110 select-none object-cover opacity-20 blur-3xl"
            />

            {/* Foreground image — floats centered at natural size. With a gallery, reserve
                side + bottom strips so the arrows and dots sit on the panel margin and never
                overlap a full-width landscape or full-height portrait image. */}
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center p-6 sm:p-10",
                hasGallery && "px-16 pb-16 sm:px-20 sm:pb-16",
              )}
            >
              <img
                src={previewImages[activeIndex]}
                alt={
                  prompt.remixAppId
                    ? `${prompt.title} app`
                    : `Design reference for ${prompt.title}`
                }
                className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
              />
            </div>

            {/* Close */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setIsPreviewOpen(false)}
              aria-label="Close preview"
              className="absolute right-4 top-4 z-20 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>

            {hasGallery && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={showPrev}
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={showNext}
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
                <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 ring-1 ring-border backdrop-blur">
                  <span className="text-xs font-medium tabular-nums text-foreground/80">
                    {activeIndex + 1} / {previewImages.length}
                  </span>
                  <span className="flex items-center gap-1">
                    {previewImages.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveIndex(i)}
                        aria-label={`Go to image ${i + 1}`}
                        className={cn(
                          "h-1.5 cursor-pointer rounded-full transition-all",
                          i === activeIndex
                            ? "w-4 bg-foreground/80"
                            : "w-1.5 bg-foreground/30 hover:bg-foreground/50",
                        )}
                      />
                    ))}
                  </span>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
